/**
 * "Financial Rep" — the Muse Group subscription-finance report, reproduced
 * visual-for-visual from the Power BI model in `apps/hle_dash/pbix_model/`.
 *
 * The structure below is NOT invented: it is the inventory of the source
 * report's 80 visuals (their types, titles, field bindings and order), read out
 * of the PBIR definition. Three pages, one shared slicer state — exactly as
 * Power BI report pages share slicers. Presentation follows OUR theme; the
 * arrangement, fields and figures follow the original.
 *
 * Everything targets **ClickHouse** (`finance_public.payouts`, 13.3M rows).
 * Two rules drive every query:
 *
 *  1. **Filter on `purchase_date`.** It is the partition key (`toYYYYMM`) and
 *     the first primary-key column, so a date filter prunes whole partitions.
 *     Filtering `purchase_datetime` instead would scan everything.
 *
 *  2. **Never alias an aggregate with a source column's name.** ClickHouse
 *     resolves bare identifiers against aliases declared earlier in the same
 *     SELECT, so `sum(gross_revenue_usd) AS gross_revenue_usd` rebinds every
 *     later reference and the query dies with "aggregate function inside
 *     another aggregate function". Every alias here is a name that is NOT a
 *     column.
 *
 * Measure definitions are the source model's, quirks included — see
 * `pbix_model/measures.md`. The notable ones are marked inline.
 */

import type { Dashboard, DataSource, Engine, Widget } from "../types";

export type FinancePage = "payout" | "taxes" | "deferred";

/** Slicer state. Mirrors the source report's slicer set; each page shows the
 *  subset that page actually carries. `undefined` = "All". */
export interface FinanceFilters {
  product?: string;
  entity?: string;
  provider?: string;
  providerCurrency?: string;
  reportType?: string;
  country?: string;
  countryTaxSegment?: string;
  type?: string;
  lengthClass?: string;
  year?: string;
  quarter?: string;
  month?: string;
}

/** Default view. The source report opens filtered to a single year, which is
 *  also what keeps the first load inside one partition range. */
export const DEFAULT_FINANCE_FILTERS: FinanceFilters = { year: "2026" };

export const FINANCE_PAGE_LABELS: Record<FinancePage, string> = {
  payout: "Payout",
  taxes: "Taxes",
  deferred: "Deferred Revenue",
};

const FACT = "finance_public.payouts";

/**
 * Engine these pages require.
 *
 * Widget SQL runs against whichever connection is active in the shell, and this
 * SQL is ClickHouse-specific (`multiIf`, `sumIf`, `toStartOfMonth`). Pointing it
 * at Postgres fails every widget with a parse error, so the UI checks this and
 * says so plainly instead.
 */
export const FINANCE_ENGINE: Engine = "clickhouse";

const esc = (s: string) => s.replace(/'/g, "''");

/** Subscription-length buckets — the model's `Length Class`, used both as a
 *  displayed column and as a slicer. */
const LENGTH_CLASS = `multiIf(subscription_duration_days = 1, 'Instant purchase',
          subscription_duration_days <= 31, '1 Month Subscription',
          subscription_duration_days <= 180, '2-6 Month Subscription',
          '6-12 Month Subscription')`;

/** Empty strings are real in `entity` (1.18M rows) and `segment_royalty`; show
 *  them as an explicit label rather than a blank cell. */
const label = (col: string) => `if(${col} = '', '—', ${col})`;

/** Date-part predicates against an arbitrary date column. */
function dateWhere(f: FinanceFilters, col: string): string[] {
  const parts: string[] = [];
  if (f.year) parts.push(`toYear(${col}) = ${Number(f.year)}`);
  if (f.quarter) parts.push(`toQuarter(${col}) = ${Number(f.quarter)}`);
  if (f.month) parts.push(`toMonth(${col}) = ${Number(f.month)}`);
  return parts;
}

/** Every non-date predicate. Split out because the Deferred page applies the
 *  date slicers to a DIFFERENT column than the other pages (see `deferredBase`). */
function dimensionWhere(f: FinanceFilters): string[] {
  const parts: string[] = [];
  if (f.product) parts.push(`product = '${esc(f.product)}'`);
  if (f.entity) parts.push(`${label("entity")} = '${esc(f.entity)}'`);
  if (f.provider) parts.push(`provider = '${esc(f.provider)}'`);
  if (f.providerCurrency)
    parts.push(`provider_currency = '${esc(f.providerCurrency)}'`);
  if (f.reportType) parts.push(`report_type = '${esc(f.reportType)}'`);
  if (f.country) parts.push(`${label("country")} = '${esc(f.country)}'`);
  if (f.countryTaxSegment)
    parts.push(`country_tax_segment = '${esc(f.countryTaxSegment)}'`);
  if (f.type) parts.push(`type = '${esc(f.type)}'`);
  if (f.lengthClass) parts.push(`${LENGTH_CLASS} = '${esc(f.lengthClass)}'`);
  return parts;
}

/** WHERE clause for the Payout and Taxes pages: dates filter `purchase_date`,
 *  which is the partition key, so a date slicer prunes whole partitions. */
export function financeWhere(f: FinanceFilters): string {
  const parts = [...dateWhere(f, "purchase_date"), ...dimensionWhere(f)];
  return parts.length ? parts.join(" AND ") : "1 = 1";
}

/**
 * The source model's currency selection, reproduced verbatim.
 *
 * QUIRK, deliberate: the EUR branch tests `gross_revenue_eur != 0` for EVERY
 * measure — commission and payout included — not the measure's own column. That
 * is what the Power BI calculated columns do, so "fixing" it here would make our
 * numbers disagree with the report we are reproducing.
 *
 * The result MIXES CURRENCIES (AUD/RUB local + EUR + USD in one sum), which is
 * why the source report always shows `provider_currency` alongside it — and why
 * every table below does the same.
 */
const cur = (local: string, eur: string, usd: string) =>
  `multiIf(provider_currency IN ('AUD','RUB'), ${local}, ` +
  `provider_currency = 'EUR' AND gross_revenue_eur != 0, ${eur}, ${usd})`;

const GROSS = cur("gross_revenue_local", "gross_revenue_eur", "gross_revenue_usd");
const COMMISSION = cur(
  "commission_amount_local",
  "commission_amount_eur",
  "commission_amount_usd",
);
const NET = cur("payout_amount_local", "payout_amount_eur", "payout_amount_usd");
const TAXES = cur("indirect_taxes_local", "indirect_taxes_eur", "indirect_taxes_usd");

/** The five payout measures, as the source tables list them. */
const PAYOUT_MEASURES = `round(sum(${GROSS}), 2) AS "Gross Revenue",
  round(sumIf(${GROSS}, type IN ('refund','dispute')), 2) AS "Refund Amount",
  round(sum(${COMMISSION}), 2) AS "Commission Amount",
  round(sum(${NET}), 2) AS "Net Revenue",
  round(sum(${TAXES}), 2) AS "Indirect Taxes"`;

/** The USD tax measures used across the Taxes page. */
const TAX_MEASURES = `round(sum(gross_revenue_usd), 2) AS "GR USD",
  round(sumIf(gross_revenue_usd, tax_rate_type = 'full'), 2) AS "GR USD Full",
  round(sumIf(gross_revenue_usd, tax_rate_type = 'reduced'), 2) AS "GR USD Reduced",
  round(sum(indirect_taxes_usd), 2) AS "Ind Taxes USD",
  round(sumIf(indirect_taxes_usd, tax_rate_type = 'full'), 2) AS "Ind Taxes Full",
  round(sumIf(indirect_taxes_usd, tax_rate_type = 'reduced'), 2) AS "Ind Taxes Reduced"`;

const sql = (query: string): DataSource => ({
  kind: "sql",
  // The active connection is chosen globally in the shell; the server resolves
  // credentials from the connector id. It must be the ClickHouse connector.
  connectionId: "clickhouse",
  query,
});

/**
 * Explicit bound for the ONE transaction-grain table.
 *
 * Aggregated tables carry no artificial cap: their cardinality is bounded by the
 * GROUP BY, and if one ever does exceed the server's `MAX_ROWS` the result now
 * arrives flagged as truncated and the widget says so. This table is different —
 * it is one row per payout over 13.3M of them, so "all of it" is never the
 * intent. Bounding it here, by revenue, keeps the query honest and its title
 * states the bound.
 */
const TRANSACTION_LIMIT = 1000;

// ---------------------------------------------------------------------------
// Widget helpers
// ---------------------------------------------------------------------------

/** KPI card bound to one column of a shared single-row query.
 *
 *  Every card on a page passes the SAME `query`, so the client dedupes them
 *  into one request — the whole card row costs a single scan. */
function card(
  id: string,
  title: string,
  query: string,
  metric: string,
  x: number,
  w: number,
): Widget {
  return {
    id,
    type: "stat",
    title,
    source: sql(query),
    encoding: { y: [], agg: "sum", metric },
    layout: { x, y: 0, w, h: 2 },
  };
}

function table(
  id: string,
  title: string,
  query: string,
  y: number,
  h = 7,
): Widget {
  return {
    id,
    type: "table",
    title,
    source: sql(query),
    encoding: { y: [], agg: "sum" },
    layout: { x: 0, y, w: 12, h },
  };
}

function chart(
  id: string,
  type: "bar" | "line" | "pie",
  title: string,
  query: string,
  x: string,
  ys: string[],
  layout: { x: number; y: number; w: number; h: number },
): Widget {
  return {
    id,
    type,
    title,
    source: sql(query),
    encoding: { x, y: ys, agg: "sum" },
    layout,
  };
}

/** `YYYY-MM` month label, sortable as text. */
const MONTH = `formatDateTime(toStartOfMonth(purchase_date), '%Y-%m') AS "Month"`;

// ---------------------------------------------------------------------------
// Page 1 — Payout: seven tables, progressively finer grain.
// ---------------------------------------------------------------------------

function payoutWidgets(f: FinanceFilters): Widget[] {
  const W = financeWhere(f);
  const from = `FROM ${FACT} WHERE ${W}`;

  const totals = `SELECT provider_currency AS "Currency", ${PAYOUT_MEASURES}
${from} GROUP BY "Currency" ORDER BY "Gross Revenue" DESC`;

  const byDims = `SELECT product AS "Product", ${label("entity")} AS "Entity", provider AS "Provider",
  provider_currency AS "Currency", report_type AS "Report type", ${PAYOUT_MEASURES}
${from} GROUP BY "Product", "Entity", "Provider", "Currency", "Report type"
ORDER BY "Gross Revenue" DESC`;

  const byMonth = `SELECT toYear(purchase_date) AS "Year", ${MONTH},
  product AS "Product", ${label("entity")} AS "Entity", provider AS "Provider",
  provider_currency AS "Currency", report_type AS "Report type", ${PAYOUT_MEASURES}
${from} GROUP BY "Year", "Month", "Product", "Entity", "Provider", "Currency", "Report type"
ORDER BY "Year" DESC, "Month" DESC, "Gross Revenue" DESC`;

  const byCountry = `SELECT product AS "Product", ${label("entity")} AS "Entity", provider AS "Provider",
  ${label("country")} AS "Country", provider_currency AS "Currency", report_type AS "Report type",
  ${PAYOUT_MEASURES}
${from} GROUP BY "Product", "Entity", "Provider", "Country", "Currency", "Report type"
ORDER BY "Gross Revenue" DESC`;

  const byPlan = `SELECT toDate(purchase_datetime) AS "Purchase date", product AS "Product",
  ${label("entity")} AS "Entity", provider AS "Provider", plan_name AS "Plan",
  ${label("country")} AS "Country", provider_currency AS "Currency", report_type AS "Report type",
  ${PAYOUT_MEASURES}
${from} GROUP BY "Purchase date", "Product", "Entity", "Provider", "Plan", "Country", "Currency", "Report type"
ORDER BY "Gross Revenue" DESC`;

  // Transaction grain. 13.3M rows exist behind this — the LIMIT is what keeps
  // it honest, and the title says which slice is shown.
  const byTransaction = `SELECT payout_id AS "Payout id", source_id AS "Source id",
  postal_code AS "Postal code", country_tax_segment AS "Tax segment",
  processed_datetime AS "Processed", toDate(purchase_datetime) AS "Purchase date",
  product AS "Product", provider AS "Provider", provider_currency AS "Currency",
  round(${GROSS}, 2) AS "Gross Revenue", round(${COMMISSION}, 2) AS "Commission Amount",
  round(${NET}, 2) AS "Net Revenue", round(${TAXES}, 2) AS "Indirect Taxes"
${from} ORDER BY "Gross Revenue" DESC LIMIT ${TRANSACTION_LIMIT}`;

  const byLength = `SELECT toYear(purchase_date) AS "Year", ${MONTH}, product AS "Product",
  ${LENGTH_CLASS} AS "Length Class", ${label("entity")} AS "Entity", provider AS "Provider",
  provider_currency AS "Currency", ${PAYOUT_MEASURES}
${from} GROUP BY "Year", "Month", "Product", "Length Class", "Entity", "Provider", "Currency"
ORDER BY "Year" DESC, "Month" DESC, "Gross Revenue" DESC`;

  return [
    table("f-p-totals", "Payouts total", totals, 0, 5),
    table("f-p-dims", "Payouts total", byDims, 5),
    table("f-p-month", "Payouts total by month", byMonth, 12),
    table("f-p-country", "Payouts by country", byCountry, 19),
    table("f-p-plan", "Payouts by plan type", byPlan, 26),
    table("f-p-tx", `Payouts by transaction (top ${TRANSACTION_LIMIT} by revenue)`, byTransaction, 33, 8),
    table("f-p-length", "Payouts by plan length by month", byLength, 41),
  ];
}

// ---------------------------------------------------------------------------
// Page 2 — Taxes: six cards, a combo, two pies, three tables.
// ---------------------------------------------------------------------------

function taxesWidgets(f: FinanceFilters): Widget[] {
  const W = financeWhere(f);
  const from = `FROM ${FACT} WHERE ${W}`;

  // `tax_rate_type` is exactly {full, reduced} over 100% of rows (verified), so
  // full + reduced always reconciles to the total — the cards must add up.
  const KPI = `SELECT ${TAX_MEASURES} ${from}`;

  const period = `SELECT ${MONTH},
  round(sum(gross_revenue_usd), 2) AS "Gross Revenue USD",
  round(sumIf(indirect_taxes_usd, tax_rate_type = 'full'), 2) AS "Indirect Taxes USD Full"
${from} GROUP BY "Month" ORDER BY "Month"`;

  const byProduct = `SELECT product AS "Product", round(sum(gross_revenue_usd), 2) AS "Gross Revenue USD"
${from} GROUP BY "Product" ORDER BY "Gross Revenue USD" DESC`;

  const byProvider = `SELECT provider AS "Provider", round(sum(gross_revenue_usd), 2) AS "Gross Revenue USD"
${from} GROUP BY "Provider" ORDER BY "Gross Revenue USD" DESC`;

  const taxesTable = `SELECT product AS "Product", ${label("entity")} AS "Entity", provider AS "Provider",
  country_tax_segment AS "Tax segment", tax_currency AS "Tax currency", report_type AS "Report type",
  ${TAX_MEASURES}
${from} GROUP BY "Product", "Entity", "Provider", "Tax segment", "Tax currency", "Report type"
ORDER BY "GR USD" DESC`;

  const byCountry = `SELECT toYear(purchase_date) AS "Year", ${MONTH}, product AS "Product",
  ${label("entity")} AS "Entity", provider AS "Provider", ${label("country")} AS "Country",
  country_tax_segment AS "Tax segment", tax_currency AS "Tax currency", report_type AS "Report type",
  ${TAX_MEASURES}
${from} GROUP BY "Year", "Month", "Product", "Entity", "Provider", "Country", "Tax segment", "Tax currency", "Report type"
ORDER BY "Ind Taxes USD" DESC`;

  // US states — the source restricts this table to the US tax segment.
  const byState = `SELECT toDate(purchase_datetime) AS "Date", product AS "Product",
  ${label("entity")} AS "Entity", provider AS "Provider", ${label("region")} AS "Region",
  country_tax_segment AS "Tax segment", tax_currency AS "Tax currency", report_type AS "Report type",
  ${TAX_MEASURES}
${from} AND country_tax_segment = 'US'
GROUP BY "Date", "Product", "Entity", "Provider", "Region", "Tax segment", "Tax currency", "Report type"
ORDER BY "Ind Taxes USD" DESC`;

  return [
    card("f-t-gr", "Gross Revenue USD", KPI, "GR USD", 0, 2),
    card("f-t-gr-full", "GR USD Full", KPI, "GR USD Full", 2, 2),
    card("f-t-gr-red", "GR USD Reduced", KPI, "GR USD Reduced", 4, 2),
    card("f-t-tax", "Indirect Taxes USD", KPI, "Ind Taxes USD", 6, 2),
    card("f-t-tax-full", "Ind Taxes USD Full", KPI, "Ind Taxes Full", 8, 2),
    card("f-t-tax-red", "Ind Taxes USD Reduced", KPI, "Ind Taxes Reduced", 10, 2),
    chart("f-t-period", "bar", "Revenue Period", period, "Month",
      ["Gross Revenue USD", "Indirect Taxes USD Full"], { x: 0, y: 2, w: 12, h: 6 }),
    chart("f-t-pie-product", "pie", "Revenue by product gross USD", byProduct,
      "Product", ["Gross Revenue USD"], { x: 0, y: 8, w: 6, h: 5 }),
    chart("f-t-pie-provider", "pie", "Revenue by provider gross USD", byProvider,
      "Provider", ["Gross Revenue USD"], { x: 6, y: 8, w: 6, h: 5 }),
    table("f-t-table", "Taxes", taxesTable, 13),
    table("f-t-country", "Indirect taxes by country", byCountry, 20),
    table("f-t-states", "Indirect taxes US by states", byState, 27),
  ];
}

// ---------------------------------------------------------------------------
// Page 3 — Deferred Revenue: four cards, a combo, two pies, two tables,
// two column charts.
// ---------------------------------------------------------------------------

/**
 * Deferred revenue recognises a subscription's revenue evenly across the days of
 * its term. Reproduced from the source model's own ClickHouse query
 * (`pbix_model/model.md` section 4), with two deliberate differences:
 *
 *  * `coalesce` guards the month comparison. In DAX `BLANK() <> date` is TRUE,
 *    so a missing start date lands in "previous month"; in SQL `NULL != x` is
 *    NULL and the row would vanish from BOTH measures. The guard keeps the
 *    totals reconciling.
 *  * The day expansion is bounded to the filtered period rather than the whole
 *    table, so a date slicer does not build a calendar for all time.
 *
 * IMPORTANT — the date slicers filter the RECOGNITION DAY (`d`), not the
 * purchase date. In the source model the Year/Quarter/Month slicers bind to the
 * Finance Deferred table's own date, so a subscription bought in December 2025
 * still contributes to 2026 — that is the entire point of deferred revenue.
 * Filtering `purchase_date` instead (as the other two pages do) silently drops
 * every carried-over subscription and understates "Previous GR USD PO"; it did,
 * by 20.6M USD, until the reconciliation against the original caught it.
 */
function deferredBase(f: FinanceFilters): string {
  // Dimensions filter the source rows; dates filter the expanded days below.
  const dims = dimensionWhere(f);
  const W = dims.length ? dims.join(" AND ") : "1 = 1";
  const dayFilter = dateWhere(f, "d.d");
  const dayWhere = dayFilter.length ? ` AND ${dayFilter.join(" AND ")}` : "";
  return `WITH calc AS (
  SELECT purchase_date AS date_start_payout,
    if(subscription_duration_days = 0, 1, subscription_duration_days) AS term_days,
    (purchase_date + term_days) - 1 AS date_end_payout,
    product, provider, entity, report_type,
    ${LENGTH_CLASS} AS length_class,
    gross_revenue_usd,
    gross_revenue_usd / term_days AS daily_share
  FROM ${FACT}
  WHERE ${W} AND product IN ('UG','MU','AUDIO_COM')
),
grouped AS (
  SELECT date_start_payout, date_end_payout, term_days, product, provider, entity,
         report_type, length_class,
         sum(gross_revenue_usd) AS revenue_at_start,
         sum(daily_share) AS revenue_per_day
  FROM calc
  GROUP BY date_start_payout, date_end_payout, term_days, product, provider, entity,
           report_type, length_class
),
days AS (
  SELECT addDays(b.d_min, n) AS d
  FROM (SELECT min(date_start_payout) AS d_min, max(date_end_payout) AS d_max FROM grouped) AS b
  ARRAY JOIN range(toUInt32(dateDiff('day', b.d_min, b.d_max) + 1)) AS n
),
spread AS (
  SELECT g.product AS product, g.provider AS provider, g.entity AS entity,
         g.report_type AS report_type, g.length_class AS length_class,
         g.date_start_payout AS date_start_payout, d.d AS d,
         g.revenue_per_day AS revenue_per_day,
         if(g.date_start_payout = d.d, g.revenue_at_start, 0) AS revenue_at_start
  FROM grouped AS g CROSS JOIN days AS d
  WHERE d.d >= g.date_start_payout AND d.d <= g.date_end_payout${dayWhere}
)`;
}

/** "Recognised in its own month" vs "carried from an earlier month". */
const CUR_MONTH = `sumIf(revenue_per_day, toStartOfMonth(d) = toStartOfMonth(coalesce(date_start_payout, d)))`;
const PREV_MONTH = `sumIf(revenue_per_day, toStartOfMonth(d) != toStartOfMonth(coalesce(date_start_payout, d)))`;

/**
 * `GR USD Deferred` is a DIFFERENCE, not a total.
 *
 * The model defines it as `SUM(gross_revenue_usd_deferred) - [Sum Gross Revenue
 * USD for Finance Deferred]` — i.e. everything recognised across the period
 * MINUS what was booked up front. Reading it as a plain sum makes it identical
 * to Payout USD (both equal the full amount, by construction) and overstates it
 * by exactly the up-front figure.
 */
const DEFERRED_DELTA = `sum(revenue_per_day) - sum(revenue_at_start)`;

function deferredWidgets(f: FinanceFilters): Widget[] {
  const base = deferredBase(f);
  const dMonth = `formatDateTime(toStartOfMonth(d), '%Y-%m') AS "Month"`;

  const KPI = `${base}
SELECT round(sum(revenue_at_start), 2) AS "Payout USD",
  round(${CUR_MONTH}, 2) AS "Current GR USD PO",
  round(${PREV_MONTH}, 2) AS "Previous GR USD PO",
  round(${DEFERRED_DELTA}, 2) AS "GR USD Deferred"
FROM spread`;

  const period = `${base}
SELECT ${dMonth},
  round(${DEFERRED_DELTA}, 2) AS "GR USD Deferred",
  round(${CUR_MONTH}, 2) AS "Current GR USD PO"
FROM spread GROUP BY "Month" ORDER BY "Month"`;

  const byProduct = `${base}
SELECT product AS "Product", round(${CUR_MONTH}, 2) AS "Current GR USD PO",
  round(${PREV_MONTH}, 2) AS "Previous GR USD PO"
FROM spread GROUP BY "Product" ORDER BY "Current GR USD PO" DESC`;

  const byProvider = `${base}
SELECT provider AS "Provider", round(${CUR_MONTH}, 2) AS "Current GR USD PO",
  round(${PREV_MONTH}, 2) AS "Previous GR USD PO"
FROM spread GROUP BY "Provider" ORDER BY "Current GR USD PO" DESC`;

  const detail = `${base}
SELECT toYear(d) AS "Year", ${dMonth}, product AS "Product",
  if(entity = '', '—', entity) AS "Entity", provider AS "Provider", report_type AS "Report type",
  round(sum(revenue_at_start), 2) AS "Payout USD",
  round(${CUR_MONTH}, 2) AS "Current GR USD PO",
  round(${PREV_MONTH}, 2) AS "Previous GR USD PO",
  round(${DEFERRED_DELTA}, 2) AS "GR USD Deferred"
FROM spread GROUP BY "Year", "Month", "Product", "Entity", "Provider", "Report type"
ORDER BY "Year" DESC, "Month" DESC, "GR USD Deferred" DESC`;

  const totalByMonth = `${base}
SELECT toYear(d) AS "Year", ${dMonth},
  round(sum(revenue_at_start), 2) AS "Payout USD",
  round(${CUR_MONTH}, 2) AS "Current GR USD PO",
  round(${PREV_MONTH}, 2) AS "Previous GR USD PO",
  round(${DEFERRED_DELTA}, 2) AS "GR USD Deferred"
FROM spread GROUP BY "Year", "Month" ORDER BY "Year", "Month"`;

  const poByLength = `${base}
SELECT ${dMonth}, length_class AS "Length Class", round(${CUR_MONTH}, 2) AS "Current GR USD PO"
FROM spread GROUP BY "Month", "Length Class" ORDER BY "Month"`;

  const sumByLength = `${base}
SELECT ${dMonth}, length_class AS "Length Class",
  round(${CUR_MONTH} + ${PREV_MONTH}, 2) AS "Current + Previous"
FROM spread GROUP BY "Month", "Length Class" ORDER BY "Month"`;

  return [
    card("f-d-payout", "Payout USD", KPI, "Payout USD", 0, 3),
    card("f-d-current", "Current GR USD PO", KPI, "Current GR USD PO", 3, 3),
    card("f-d-previous", "Previous GR USD PO", KPI, "Previous GR USD PO", 6, 3),
    card("f-d-deferred", "GR USD Deferred", KPI, "GR USD Deferred", 9, 3),
    chart("f-d-period", "bar", "Gross Revenue USD Deferred", period, "Month",
      ["GR USD Deferred", "Current GR USD PO"], { x: 0, y: 2, w: 12, h: 6 }),
    chart("f-d-pie-product", "pie", "Gross revenue USD deferred by product", byProduct,
      "Product", ["Current GR USD PO"], { x: 0, y: 8, w: 6, h: 5 }),
    chart("f-d-pie-provider", "pie", "Gross revenue USD deferred by provider", byProvider,
      "Provider", ["Current GR USD PO"], { x: 6, y: 8, w: 6, h: 5 }),
    table("f-d-detail", "Gross revenue USD deferred", detail, 13),
    table("f-d-total", "Gross revenue USD deferred total by month", totalByMonth, 20),
    chart("f-d-po-length", "bar", "Gross revenue USD PO", poByLength, "Month",
      ["Current GR USD PO"], { x: 0, y: 27, w: 12, h: 5 }),
    chart("f-d-sum-length", "bar", "Gross revenue USD Current month + Previous Month",
      sumByLength, "Month", ["Current + Previous"], { x: 0, y: 32, w: 12, h: 5 }),
  ];
}

// ---------------------------------------------------------------------------

const PAGE_BUILDERS: Record<FinancePage, (f: FinanceFilters) => Widget[]> = {
  payout: payoutWidgets,
  taxes: taxesWidgets,
  deferred: deferredWidgets,
};

/** Build one page of the Financial Rep dashboard for the current slicer state. */
export function buildFinanceDashboard(
  page: FinancePage,
  f: FinanceFilters,
): Dashboard {
  return {
    id: `finance-${page}`,
    name: `Financial Rep — ${FINANCE_PAGE_LABELS[page]}`,
    widgets: PAGE_BUILDERS[page](f),
  };
}

// ---------------------------------------------------------------------------
// Slicers
// ---------------------------------------------------------------------------

/** Slicer keys, in the order the source report lays them out. */
export const FINANCE_SLICERS = [
  { key: "product", label: "Product" },
  { key: "entity", label: "Entity" },
  { key: "provider", label: "Provider" },
  { key: "providerCurrency", label: "Provider Currency" },
  { key: "reportType", label: "Report Type" },
  { key: "country", label: "Country" },
  { key: "countryTaxSegment", label: "Country Tax Segment" },
  { key: "type", label: "Type" },
  { key: "lengthClass", label: "Subscription Length" },
  { key: "year", label: "Year" },
  { key: "quarter", label: "Quarter" },
  { key: "month", label: "Month" },
] as const;

export type FinanceSlicerKey = (typeof FINANCE_SLICERS)[number]["key"];

/** Which slicers each page carries, matching the source report exactly. */
export const PAGE_SLICERS: Record<FinancePage, FinanceSlicerKey[]> = {
  payout: ["product", "entity", "provider", "providerCurrency", "reportType",
    "country", "type", "year", "quarter", "month"],
  taxes: ["product", "entity", "provider", "providerCurrency", "reportType",
    "country", "countryTaxSegment", "year", "quarter", "month"],
  deferred: ["product", "entity", "provider", "providerCurrency", "reportType",
    "type", "lengthClass", "year", "quarter", "month"],
};

export type FinanceSlicerOptions = Record<FinanceSlicerKey, string[]>;

export const EMPTY_FINANCE_OPTIONS: FinanceSlicerOptions = Object.fromEntries(
  FINANCE_SLICERS.map((s) => [s.key, [] as string[]]),
) as FinanceSlicerOptions;

/**
 * Slicer values, one scan per dimension, combined in a subquery.
 *
 * The UNION is wrapped so the ORDER BY sorts the COMBINED result. A trailing
 * `ORDER BY` after `UNION ALL` binds to the LAST branch only — in ClickHouse
 * that is not a silent mis-sort but an outright failure ("Missing columns").
 *
 * `plan_name` is deliberately absent: ~150k distinct values would make a
 * dropdown ship 150k rows to the client. It needs a search control instead.
 */
export const FINANCE_SLICER_SQL = `SELECT dim, value, n FROM (
  SELECT 'product' AS dim, product AS value, count() AS n FROM ${FACT} GROUP BY value
  UNION ALL SELECT 'entity' AS dim, ${label("entity")} AS value, count() AS n FROM ${FACT} GROUP BY value
  UNION ALL SELECT 'provider' AS dim, provider AS value, count() AS n FROM ${FACT} GROUP BY value
  UNION ALL SELECT 'providerCurrency' AS dim, provider_currency AS value, count() AS n FROM ${FACT} GROUP BY value
  UNION ALL SELECT 'reportType' AS dim, report_type AS value, count() AS n FROM ${FACT} GROUP BY value
  UNION ALL SELECT 'countryTaxSegment' AS dim, country_tax_segment AS value, count() AS n FROM ${FACT} GROUP BY value
  UNION ALL SELECT 'type' AS dim, type AS value, count() AS n FROM ${FACT} GROUP BY value
  UNION ALL SELECT 'lengthClass' AS dim, ${LENGTH_CLASS} AS value, count() AS n FROM ${FACT} GROUP BY value
  UNION ALL SELECT 'country' AS dim, ${label("country")} AS value, count() AS n FROM ${FACT} GROUP BY value
  UNION ALL SELECT 'year' AS dim, toString(toYear(purchase_date)) AS value, count() AS n FROM ${FACT} GROUP BY value
  UNION ALL SELECT 'quarter' AS dim, toString(toQuarter(purchase_date)) AS value, count() AS n FROM ${FACT} GROUP BY value
  UNION ALL SELECT 'month' AS dim, toString(toMonth(purchase_date)) AS value, count() AS n FROM ${FACT} GROUP BY value
) ORDER BY dim ASC, n DESC`;

/** Values that read better sorted naturally than by row count. */
const NUMERIC_DIMS: ReadonlySet<string> = new Set(["year", "quarter", "month"]);

export function parseFinanceSlicerOptions(
  rows: Record<string, unknown>[],
): FinanceSlicerOptions {
  const out = Object.fromEntries(
    FINANCE_SLICERS.map((s) => [s.key, [] as string[]]),
  ) as FinanceSlicerOptions;
  for (const row of rows) {
    const dim = String(row.dim ?? "") as FinanceSlicerKey;
    const value = String(row.value ?? "");
    if (value && dim in out) out[dim].push(value);
  }
  for (const dim of NUMERIC_DIMS) {
    const key = dim as FinanceSlicerKey;
    out[key].sort((a, b) => Number(a) - Number(b));
  }
  return out;
}
